/**
 * FrameItemMarkers — Renders ItemMarker overlays for all marked/cart items
 * that project onto the currently displayed frame.
 *
 * Follows the sealed-file extraction pattern: NewModeViewer just renders
 * <FrameItemMarkers /> instead of inline logic.
 */
import { useMemo, useCallback } from 'react'
import { useCartStore } from '@/stores/cartStore'
import { useAppStore } from '@/store/appStore'
import { useWishlistStore } from '@/stores/wishlistStore'
import { project3DPointToCamera } from '@utils/cameraProjection'
import { ItemMarker } from '@/features/delivery/components/ItemMarker'
import { WishMarker } from '@/features/delivery/components/WishMarker'
import type { CartItem } from '@/features/delivery/types'

interface Camera {
  intrinsics: { width: number; height: number; fx?: number; fy?: number; cx?: number; cy?: number }
  position: [number, number, number]
  [key: string]: any
}

interface FrameItemMarkersProps {
  frameIndex: number
  frameCamera: Camera
  frameDisplayWidth: number
  frameDisplayHeight: number
  shopId: string
  onNavigateToItem?: (item: CartItem) => void
}

export function FrameItemMarkers({
  frameIndex,
  frameCamera,
  frameDisplayWidth,
  frameDisplayHeight,
  shopId,
  onNavigateToItem,
}: FrameItemMarkersProps) {
  const items = useCartStore((s) => s.items)
  const currentSectionId = useAppStore((s) => s.currentSectionId)
  const markerLabelsVisible = useAppStore((s) => s.markerLabelsVisible)
  const activeWishlists = useWishlistStore((s) => s.activeWishlists)

  // All items in this shop+section (for prev/next navigation)
  const allShopItems = useMemo(() => {
    return items
      .filter((item) => item.shopId === shopId && item.sectionId === String(currentSectionId))
      .sort((a, b) => a.frameIndex - b.frameIndex || a.createdAt.localeCompare(b.createdAt))
  }, [items, shopId, currentSectionId])

  const handleNavigateToItem = useCallback((item: CartItem) => {
    onNavigateToItem?.(item)
  }, [onNavigateToItem])

  // Get items visible on this frame and compute display positions
  const visibleItems = useMemo(() => {
    const imageWidth = frameCamera.intrinsics.width   // e.g. 450
    const imageHeight = frameCamera.intrinsics.height  // e.g. 800
    const scaleX = frameDisplayWidth / imageWidth
    const scaleY = frameDisplayHeight / imageHeight

    return items
      .filter((item) => item.shopId === shopId && item.sectionId === String(currentSectionId))
      .map((item) => {
        let imgX: number | null = null
        let imgY: number | null = null

        // Check if this item's original frame matches
        if (item.frameIndex === frameIndex) {
          imgX = item.imagePixelX
          imgY = item.imagePixelY
        }
        // Check pre-computed projections
        else if (item.projections[frameIndex]) {
          const [px, py] = item.projections[frameIndex]
          imgX = px
          imgY = py
        }
        // Try live 3D projection
        else if (item.point3D) {
          try {
            const projected = project3DPointToCamera(item.point3D, frameCamera)
            if (projected) {
              const [px, py] = projected
              // Bounds check (with margin)
              if (px >= -50 && px <= imageWidth + 50 && py >= -50 && py <= imageHeight + 50) {
                imgX = px
                imgY = py
              }
            }
          } catch {
            // projection failure — skip
          }
        }

        if (imgX === null || imgY === null) return null

        return {
          item,
          displayX: imgX * scaleX,
          displayY: imgY * scaleY,
          displayRadius: item.radius * scaleX,
        }
      })
      .filter(Boolean) as { item: typeof items[0]; displayX: number; displayY: number; displayRadius: number }[]
  }, [items, frameIndex, frameCamera, frameDisplayWidth, frameDisplayHeight, shopId, currentSectionId])

  // ── Wish list items visible on this frame ──
  const visibleWishItems = useMemo(() => {
    const imageWidth = frameCamera.intrinsics.width
    const imageHeight = frameCamera.intrinsics.height
    const scaleX = frameDisplayWidth / imageWidth
    const scaleY = frameDisplayHeight / imageHeight

    const result: { wishlistToken: string; color: string; senderName: string; item: any; displayX: number; displayY: number; displayRadius: number }[] = []

    for (const wl of activeWishlists) {
      if (!wl.visible) continue
      for (const wItem of wl.items) {
        if (wItem.sectionId !== String(currentSectionId)) continue
        if (wItem.imagePixelX === 0 && wItem.imagePixelY === 0) continue

        let imgX: number | null = null
        let imgY: number | null = null

        // Exact frame match — use stored pixel coords
        if (wItem.frameIndex === frameIndex) {
          imgX = wItem.imagePixelX
          imgY = wItem.imagePixelY
        }
        // 3D projection — follow item across frames (requires point3D stored at create time)
        else if (wItem.point3D) {
          try {
            const projected = project3DPointToCamera(wItem.point3D, frameCamera)
            if (projected) {
              const [px, py] = projected
              const imageWidth = frameCamera.intrinsics.width
              const imageHeight = frameCamera.intrinsics.height
              if (px >= -50 && px <= imageWidth + 50 && py >= -50 && py <= imageHeight + 50) {
                imgX = px
                imgY = py
              }
            }
          } catch {
            // projection failure — skip
          }
        }

        if (imgX === null || imgY === null) continue

        result.push({
          wishlistToken: wl.token,
          color: wl.color,
          senderName: wl.senderName,
          item: wItem,
          displayX: imgX * scaleX,
          displayY: imgY * scaleY,
          displayRadius: wItem.radius * scaleX,
        })
      }
    }
    return result
  }, [activeWishlists, frameIndex, frameCamera, frameDisplayWidth, frameDisplayHeight, currentSectionId])

  if (visibleItems.length === 0 && visibleWishItems.length === 0) return null

  return (
    <>
      {/* Own cart item markers */}
      {visibleItems.map(({ item, displayX, displayY, displayRadius }, idx) => (
        <ItemMarker
          key={item.id}
          item={item}
          displayX={displayX}
          displayY={displayY}
          displayRadius={displayRadius}
          index={idx + 1}
          allItems={allShopItems}
          onNavigateToItem={handleNavigateToItem}
          labelsHidden={!markerLabelsVisible}
        />
      ))}

      {/* Wish list markers (read-only, color-coded per person) */}
      {visibleWishItems.map(({ wishlistToken, color, senderName, item, displayX, displayY, displayRadius }, idx) => (
        <WishMarker
          key={`wish-${wishlistToken}-${idx}`}
          item={item}
          color={color}
          senderName={senderName}
          displayX={displayX}
          displayY={displayY}
          displayRadius={displayRadius}
          index={idx + 1}
          labelsHidden={!markerLabelsVisible}
        />
      ))}
    </>
  )
}
